import { uploadFlowstepData } from 'src/api';

import { RangeType, NoDataError, ResultDataError } from 'shared/utils/flowstep';
import { rangeToRowSpan, getIntersectionRowSpans, intersectSpanWithSpans } from 'shared/excel/span';
import SpansTree from 'shared/excel/spansTree';


import {
  DEFAULT_RESULTS_ROW_SYMBOLS_COUNT,
  DEFAULT_CELL_SYMBOLS_COUNT,
  PUSH_FLOWSTEP_RESULT_HEADER,
  getResultHeaders,
  Limits,
  executeInBatches,
  executeInBatchesWithSpansTree,
  fieldTypesToSymbolsCount,
  getNewRangeOptions,
  getResultTableDelta,
} from 'shared/excel/runtime/flowstepExecution';


import { clearAllFilters, applyFilters } from 'src/services/runtime/dataTable';
import { Flowstep } from 'src/services/runtime/flowstep';
import { loadRangesValues, loadTableValues } from 'src/services/substitution';


export class PushFlowstep extends Flowstep {
  // Data about indexes values were loaded from (and where results should be put) are stored in the format of contigous spans tree.
  // The first number is start index (relative to the this.dataTable.dataBodyRange, so 0 is the beginning of the data in the table)
  // of the span, the second one is its length.
  // For instance, user executes flowstep with rangeToApply.type === 'Rows' and rows defined as 1-50, 60-95, 120-200. Then, depending on
  // a visibility of cells, we might have the following tree. The data will be fetched from worksheet according to the indexes on 3rd level:
  //
  // 1--------------------------------------------------200 +-->   The whole data source tree -> this.dataSourceTree
  // |                                                  |
  // 1---------50     60--------------95       120------200 +-->   Ranges === excel.getRange()
  // |         |       |              |          |      |
  // 1--24  30-50     60--75 70-83 90-95*      120------200 +-->   Visible views  === excel.getRange().getVisibleView() === values for request
  //
  // To access the next level from the current one you can use 'children' property
  // For instance, to access 3rd level (view) in this example marked with '*' you need to call this.dataSourceTree.children[1].children[2]
  // See SpansTree class for details

  constructor(flowstep, meta, parent) {
    super(flowstep, meta, parent);

    this.resultsTable = null;
    this.savedFilters = [];

    this.loaders = {
      objectLoader: async (objectName) => await this.salesforceStore.fetchObjectData({ objectName }),
      queryLoader: async (queries) => (await this.salesforceStore.runQueries(queries)).results,
      tablesLoader: async (tables) => await loadTableValues(this.context, tables, this.limits),
      rangesLoader: async (ranges) => await loadRangesValues(this.context, ranges, this.worksheet.id, this.limits),
    };

    this.resultIndexes = {
      status: this.resultTableHeaders.findIndex(x => x.key === PUSH_FLOWSTEP_RESULT_HEADER.Result.key),
      id: this.resultTableHeaders.findIndex(x => x.key === PUSH_FLOWSTEP_RESULT_HEADER.Id.key)
    };

    this.selectedRanges = meta.selectedRanges;
  }

  get resultTableHeaders() { return getResultHeaders(this.type); }
  get showResultsTable() { return this.flowstep.options.showOperationResultsTable ?? true; }

  // override Flowstep
  async _runFlowstep_() {
    let timestamp;
    try {


      this.fireEvent('precollecting');
      await this.preCollectData();

      this.fireEvent('collecting');
      const data = await this.collectData();

      this.fireEvent('preprocessing');
      const requestOptions = await this.preProcessData(data);

      this.fireEvent('remote-starting');
      const { url: resultsUrl, timestamp: resultTimestamp } = await this.startRemoteExecution(requestOptions);
      timestamp = resultTimestamp;

      this.fireEvent('remote-waiting');
      await this.postCollectData();
      await this._preInsertResults_?.(timestamp);

      this.fireEvent('remote-receiving');
      await this.receiveResults(resultsUrl);

      await this.tasks.waitForAll();

      this.fireEvent('postprocessing');
      await this.postProcessData(timestamp);

      this.checkResultErrors();

      return { timestamp, warnings: [] };
    }
    catch (err) {
      throw err;
    }
    finally {
      await this.postExecution(timestamp);
    }
  }

  // Default implementation
  async _processChunk_(values) {
    const chunkDataSourceTree = this.dataSourceTree.pickCount(values.length);

    this.collectResultErrors(values, chunkDataSourceTree, this.errorMapper.bind(this));

    for (const rangeVisibleTree of chunkDataSourceTree.children) {
      const rangeValues = values.splice(0, rangeVisibleTree.getValuesCount());
      const { index, count } = rangeVisibleTree;

      if (this.showResultsTable) {
        this.resultsTable
          .getRows(index, count)
          .getVisibleView()
          .values = rangeValues;
      }
    }
  }

  async preCollectData() {
    this.dataTable.enableAutomaticRangesRecalculation(false);

    await this.dataTable.pushAndUnhideColumns();
    const { options, deltas } = getResultTableDelta(this.dataTable.range.columnCount, this.resultTableHeaders);
    this.resultsTable = await this.dataTable.getOffsetAndResizedDataTable(deltas, options);
  }

  async postCollectData() {
    if (this.showResultsTable) {
      this.resultsTable.setHeaders(this.resultTableHeaders.map(x => x.header));
      await this.resultsTable.pushAndUnhideColumns();
      this.resultSuccessRange = this.resultsTable.range;
    }

    if (this.disableFilters) {
      this.savedFilters = await clearAllFilters(this.dataTable.worksheet);
    }
  }

  async postProcessData() {
    if (this.disableFilters) {
      await applyFilters(this.savedFilters);
    }
  }

  async postExecution(lastUpdateTimestamp) {
    await this.setTimestampCellValue(lastUpdateTimestamp);
    await this.dataTable.popAndHideColumns();
    if (this.showResultsTable) {
      await this.resultsTable.popAndHideColumns();
    }
  }

  async collectData() {
    const headers = await this.dataTable.getHeaders();
    const fieldTypes = await this._applyFlowstepData_(headers);
    const symbolsCount = fieldTypesToSymbolsCount(fieldTypes);

    await this.fetchDataSourceTree();
    if (this.dataSourceTree.isEmpty()) {
      throw new NoDataError();
    }

    const objects = await executeInBatchesWithSpansTree(
      symbolsCount,
      this.limits,
      this.dataSourceTree,
      (tree) => tree.children.map(rangeTree => this._loadValues_(this.dataTable.getRows(rangeTree.index, rangeTree.count), rangeTree)),
      (result) => result.flatMap(x => this._mapValues_(x)),
      async () => await this.context.sync());


    const request = await this._buildRequest_(objects);
    // const apiBatchesCount = symbolsCountToBatches(symbolsCount, this.rowsCount, Limits.LambdaApiAsync).length;
    const stringifiedObjects = JSON.stringify(request.objects);
    const objectsSizeInBytes = stringifiedObjects.length * 2.1;

    return { ...request, stringifiedObjects, isSmall: objectsSizeInBytes < Limits.LambdaApiAsync.size };
  }

  async preProcessData(data) {
    const { objects, stringifiedObjects, options, isSmall } = data;
    const request = { options };
    if (!isSmall) {
      const fileKey = await uploadFlowstepData(stringifiedObjects, this.userStoreToken, this.cancelToken);
      Object.assign(request, { fileKey });
    }
    else {
      Object.assign(request, { objects });
    }

    return request;
  }

  async processCsvChunk(values) {
    await executeInBatches(DEFAULT_RESULTS_ROW_SYMBOLS_COUNT, this.limits, values.length,
      async (chunkSize) => {
        const chunkValues = values.splice(0, chunkSize);
        const chunkValuesCount = chunkValues.length;
        this.fireEvent('processing-chunk');
        await this._processChunk_(chunkValues);
        this.processedRowCount += chunkValuesCount;
      },
      (x) => x,
      async () => await this.dataTable.sync());
  }

  async fetchDataSourceTree() {
    const { rangeToApply } = this.flowstep.options;
    const task = async () => {
      switch (rangeToApply.type) {
        case RangeType.Selected: return await this.getSelectedSpans();
        case RangeType.Rows: return await this.getRowsSpans(rangeToApply.spans);
        case RangeType.Existing:
        case RangeType.New: return await this.detectNewOnlySpans(rangeToApply);
        case RangeType.All:
        default: return [rangeToRowSpan(this.dataTable.dataBodyRange)];
      }
    };

    const rangeSpans = await task();
    // console.log('Spans collected: ', rangeToApply.type, rangeSpans);

    if (!this.dataSourceTree) { // For 'New'/'Existing' modes this data have been already fetched
      this.dataSourceTree = await this.fetchVisibleTree(rangeSpans);
    }
    this.rowToProcessCount = this.dataSourceTree.getValuesCount();
  }

  async fetchVisibleTree(rangeSpans) {
    const tree = new SpansTree();

    for (const span of rangeSpans) {
      const visibleIndexes = await executeInBatchesWithSpansTree(
        DEFAULT_CELL_SYMBOLS_COUNT,
        this.limits,
        SpansTree.fromSpan(span),
        async (rangeTree) => {
          // rangeSpans are in ABSOLUTE indexes
          const { rowIndex } = this.dataTable.toRelativeIndexes({ rowIndex: rangeTree.index });
          const range = this.dataTable.getRows(rowIndex, rangeTree.count);
          return await this.dataTable.getVisibleRowIndexes(range);
        });


      tree.addChild(SpansTree.getParentTreeFromIndexes(visibleIndexes));
    }

    // console.log('Fetched visible tree: ', tree);
    return tree;
  }

  async getSelectedSpans() {
    let activeWs;
    let areas;
    if (this.selectedRanges) {
      activeWs = this.context.workbook.worksheets.getItem(this.selectedRanges.worksheetId).load(['id', 'name']);
      areas = { items: this.selectedRanges.ranges.map(x => activeWs.getRange(x.shortAddress)) };
    }
    else {
      const selectedRanges = this.context.workbook.getSelectedRanges();
      activeWs = selectedRanges.worksheet.load(['id', 'name']);
      areas = selectedRanges.areas.load(['items']);
    }

    const flowstepWs = this.dataTable.worksheet.load(['id', 'name']);
    await this.context.sync();

    if (activeWs.id !== flowstepWs.id) {
      throw new NoDataError(`'Selected' mode of range to apply: worksheet of flowstep ('${flowstepWs.name}') and active worksheet with selection ('${activeWs.name}') are not the same`);
    }

    const ranges = areas.items.map(x => x.load(['rowIndex', 'rowCount', 'columnIndex', 'columnCount']));
    await this.context.sync();

    return getIntersectionRowSpans(this.dataTable.dataBodyRange, ranges);
  }

  async getRowsSpans(spans) {
    return intersectSpanWithSpans(rangeToRowSpan(this.dataTable.dataBodyRange), spans);
  }

  async detectNewOnlySpans(rangeToApply) {
    // First load all visible rows
    const dataSourceTree = await this.fetchVisibleTree([rangeToRowSpan(this.dataTable.dataBodyRange)]);
    const { filterFunction, isResultTable, columnIndex } = getNewRangeOptions(rangeToApply);
    const table = isResultTable ? this.resultsTable : this.dataTable;
    const tableColumn = await table.getOffsetAndResizedDataTable({ columnOffset: columnIndex, columnDelta: 1 }, { useAbsoluteDelta: true });

    // Then load the column in batches to detect empty/non-empty values
    const criterias = (await executeInBatchesWithSpansTree(
      DEFAULT_CELL_SYMBOLS_COUNT,
      this.limits,
      dataSourceTree,
      (tree) => tree.children.map(rangeTree => tableColumn.getRows(rangeTree.index, rangeTree.count).getVisibleView().load(['values'])),
      (loaded) => loaded.flatMap(x => x.values),
      async () => await this.context.sync()))
      .map(row => filterFunction(row[0])) // pick only those row, which are empty/non-empty

    // Now we have an array of true/false, where true - pick this row as data source, false - ignore
    // Split our visible rows by this criteria
    this.dataSourceTree = dataSourceTree.pickByCriteria(criterias).toPick;
    return dataSourceTree;
  }

  getResultStatus(row) {
    return row[this.resultIndexes.status];
  }

  getResultId(row) {
    return row[this.resultIndexes.id];
  }

  errorMapper(row) {
    const error = this.getResultStatus(row);
    const id = this.getResultId(row);
    return error.startsWith('Error')
      ? !!id ? `${id} : ${error}` : error
      : '';
  }

  collectResultErrors(values, dataSourceTree, criteria) {
    const resultErrorsOrEmpty = values.map(row => criteria(row));
    const firstErrorIndex = resultErrorsOrEmpty.findIndex(x => !!x);
    const hasErrors = -1 !== firstErrorIndex;

    if (hasErrors) {
      this.resultErrors.push(...resultErrorsOrEmpty.reduce((acc, x, i) => {
        x && acc.push({ error: x, rowIndex: dataSourceTree.getIndexAtPosition(i) })
        return acc;
      }, []));
    }

    return { hasErrors, resultErrorsOrEmpty };
  }

  checkResultErrors() {
    if (!this.resultErrors.length) {
      return;
    }

    this.resultErrors.sort((a, b) => a.rowIndex - b.rowIndex);
    const toAbsoluteIndexes = (rowIndex) => this.resultsTable.toAbsoluteIndexes({ rowIndex, columnIndex: 0 });

    const messages = this.resultErrors.map(({ rowIndex: relativeRowIndex, error }) => {
      const { rowIndex } = toAbsoluteIndexes(relativeRowIndex);
      return `Row ${rowIndex + 1} : ${error}`;
    });

    if (this.showResultsTable && !this.resultErrorRange) {
      const { rowIndex: firstRelativeRowIndex } = this.resultErrors[0];
      const { rowIndex, columnIndex } = toAbsoluteIndexes(firstRelativeRowIndex);
      this.resultErrorRange = this.worksheet.getRangeByIndexes(rowIndex, columnIndex, 1, this.resultsTable.range.columnCount);
    }

    throw new ResultDataError(messages, this.resultErrorRange);
  }
}